/**
 * FSRNode — TSL implementation of FSR 1 EASU + RCAS.
 *
 * Two TSL nodes:
 *   - `createEASUNode` — 12-tap edge-adaptive spatial upsample
 *   - `createRCASNode` — 3×3 unsharp with neighborhood-clamped lobe
 *
 * Caller composes them in their post chain:
 *   const easu = createEASUNode({ srcColor: scenePassTextureNode, … });
 *   const rcas = createRCASNode({ srcColor: convertToTexture(easu.node), … });
 *   // Bind rcas.node as the final post output
 *
 * `srcColor` must be a texture NODE for anything a pass renders (the pass
 * texture node, an RTT node) — see `sampleAt`. A raw Texture is only right for
 * an image that exists without a producer in the graph.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, vec2, vec3, vec4, float, uniform,
  texture, max, min, mix, clamp, abs, screenUV,
} from 'three/tsl';

/**
 * Sample `src` at `uv`. A texture NODE (a pass texture node, an RTT node) is
 * sampled through its own `.sample()`, a raw `Texture` through `texture()`.
 *
 * WHY: three renders a PassNode only when a PassTextureNode that points at it
 * is part of the built graph (`PassTextureNode.setup` registers the pass as a
 * node property, and that is what puts its `updateBefore` in the frame). The
 * first FSR build sampled the pass's raw render-target texture with
 * `texture(passTextureNode.value)`, so no node in the graph referenced the
 * pass: the scene was never rendered, EASU read a cleared target, and every
 * tier drew a black frame under a live HUD — 3 triangles per frame, no error
 * on either backend ("now the screen is black", 2026-09-08).
 */
function sampleAt(src: unknown, uv: unknown): any {
  const s = src as { isTextureNode?: boolean; sample?: (uv: unknown) => unknown } | null;
  if (s && s.isTextureNode === true && typeof s.sample === 'function') return s.sample(uv);
  return texture(src as THREE.Texture, uv as never);
}

export interface EASUUniforms {
  srcSize: ReturnType<typeof uniform>;
  dstSize: ReturnType<typeof uniform>;
}

export interface EASUNodeResult {
  node: unknown;
  uniforms: EASUUniforms;
}

/**
 * Build the EASU spatial upsampling node.
 *
 * Reads `srcColor` at the source resolution and outputs the upsampled
 * color at `dstSize` (driven by `screenUV`).
 *
 * The EASU kernel is a 12-tap edge-adaptive interpolator that uses the
 * local gradient to bias samples toward the edge direction, preserving
 * fine detail. Reference: AMD GPUOpen FidelityFX FSR 1.0.
 */
export function createEASUNode(args: {
  srcColor: unknown;
  srcWidth: number;
  srcHeight: number;
  dstWidth: number;
  dstHeight: number;
}): EASUNodeResult {
  const srcSizeU = uniform(vec2(args.srcWidth, args.srcHeight));
  const dstSizeU = uniform(vec2(args.dstWidth, args.dstHeight));

  const easuFn: any = Fn(() => {
    const uvOut = screenUV;
    // Source-space pixel coordinate
    const srcCoord = uvOut.mul(srcSizeU).sub(0.5);
    const srcBase = vec2(
      (srcCoord.x as any).floor(),
      (srcCoord.y as any).floor(),
    );
    const f = vec2(
      srcCoord.x.sub(srcBase.x),
      srcCoord.y.sub(srcBase.y),
    );
    const invSrcSize = vec2(float(1).div(srcSizeU.x), float(1).div(srcSizeU.y));

    // Gather 12 nearby samples (4 cardinals + 4 diagonals + 4 extra for direction estimate)
    const sample = (dx: number, dy: number) => {
      const uv = vec2(
        srcBase.x.add(float(dx)).add(0.5).mul(invSrcSize.x),
        srcBase.y.add(float(dy)).add(0.5).mul(invSrcSize.y),
      );
      return sampleAt(args.srcColor, uv);
    };
    // 12-tap "+" + diagonals pattern
    const c00: any = sample(-1, -1); const c10: any = sample(0, -1); const c20: any = sample(1, -1); const c30: any = sample(2, -1);
    const c01: any = sample(-1,  0); const c11: any = sample(0,  0); const c21: any = sample(1,  0); const c31: any = sample(2,  0);
    const c02: any = sample(-1,  1); const c12: any = sample(0,  1); const c22: any = sample(1,  1); const c32: any = sample(2,  1);

    // Estimate edge direction from luma differences
    const lumaWeight = vec3(0.299, 0.587, 0.114);
    const l11 = c11.rgb.dot(lumaWeight);
    const l21 = c21.rgb.dot(lumaWeight);
    const l12 = c12.rgb.dot(lumaWeight);
    const l22 = c22.rgb.dot(lumaWeight);

    // Cardinal differences for direction estimate
    const dx = abs(l21.sub(l11)).add(abs(l22.sub(l12)));
    const dy = abs(l12.sub(l11)).add(abs(l22.sub(l21)));

    // Edge-aware blend weight: favor along-edge interpolation
    const edgeStr = max(dx, dy);
    const horiz = float(1).sub(clamp(edgeStr.mul(2), float(0), float(1)));

    // Bilinear sample at the 2×2 inner neighborhood
    const m00 = c11.rgb;
    const m10 = c21.rgb;
    const m01 = c12.rgb;
    const m11 = c22.rgb;
    const top = mix(m00, m10, f.x);
    const bot = mix(m01, m11, f.x);
    const baseline = mix(top, bot, f.y);

    // Edge-direction-biased extras (use the outer ring for soft edge enhancement)
    const ring = (c00.rgb.add(c10.rgb).add(c20.rgb).add(c30.rgb)
      .add(c01.rgb).add(c31.rgb)
      .add(c02.rgb).add(c32.rgb)).mul(0.125);

    // Blend: in flat regions favor ring (smoothing); on edges favor baseline (preserve sharp)
    const result = mix(baseline, ring, horiz.mul(0.15));

    // Clamp to local min/max to prevent ringing
    const minRGB = min(min(min(m00, m10), m01), m11);
    const maxRGB = max(max(max(m00, m10), m01), m11);
    const clamped = vec3(
      clamp(result.x, minRGB.x, maxRGB.x),
      clamp(result.y, minRGB.y, maxRGB.y),
      clamp(result.z, minRGB.z, maxRGB.z),
    );

    return vec4(clamped, float(1));
  });

  return {
    node: easuFn(),
    uniforms: { srcSize: srcSizeU, dstSize: dstSizeU },
  };
}

export interface RCASUniforms {
  sharpness: ReturnType<typeof uniform>;
  invSrcSize: ReturnType<typeof uniform>;
}

/** AMD FSR1 `FSR_RCAS_LIMIT` = 0.25 − 1/16: max lobe magnitude before sharpness. */
export const RCAS_LOBE_LIMIT = 0.25 - 1 / 16;

/**
 * CPU mirror of the RCAS lobe / unsharp denominator (exported for tests —
 * the TSL kernel above uses the identical expression).
 */
export function rcasLobeCpu(hitMin: number, hitMax: number, sharpness: number): { lobe: number; denom: number } {
  const raw = -Math.max(-hitMin, hitMax);
  const lobe = Math.max(-RCAS_LOBE_LIMIT, Math.min(0, raw)) * sharpness;
  return { lobe, denom: 1 + 4 * lobe };
}

export interface RCASNodeResult {
  node: unknown;
  uniforms: RCASUniforms;
}

/**
 * Build the RCAS sharpening node.
 *
 * Reads `srcColor` and applies a 3×3 unsharp filter with neighborhood-
 * clamped sharpening — bright/dark regions are protected from amplifying
 * noise.
 *
 * The output is clamped to the [min, max] of the 4-neighborhood to
 * prevent overshoot.
 */
export function createRCASNode(args: {
  srcColor: unknown;
  srcWidth: number;
  srcHeight: number;
  sharpness: number;
}): RCASNodeResult {
  const sharpnessU = uniform(args.sharpness);
  const invSrcSizeU = uniform(vec2(1 / args.srcWidth, 1 / args.srcHeight));

  const rcasFn: any = Fn(() => {
    const uv = screenUV;
    const c: any = sampleAt(args.srcColor, uv);
    const n: any = sampleAt(args.srcColor, vec2(uv.x, uv.y.sub(invSrcSizeU.y)));
    const s: any = sampleAt(args.srcColor, vec2(uv.x, uv.y.add(invSrcSizeU.y)));
    const e: any = sampleAt(args.srcColor, vec2(uv.x.add(invSrcSizeU.x), uv.y));
    const w: any = sampleAt(args.srcColor, vec2(uv.x.sub(invSrcSizeU.x), uv.y));

    const minRGB = min(min(min(n.rgb, s.rgb), e.rgb), w.rgb);
    const maxRGB = max(max(max(n.rgb, s.rgb), e.rgb), w.rgb);

    // Per-channel lobe weight (use green channel as luma proxy — cheaper than dot)
    const hitMin = min(min(minRGB.g, c.g).div(maxRGB.g.add(float(1e-6))), float(1));
    const hitMax = float(1).sub(max(maxRGB.g, c.g)).div(float(1).sub(minRGB.g).add(float(1e-6)));
    // WHY the clamp: unclamped, lobe = −hitMax·sharpness reaches −sharpness on
    // a dark high-contrast pixel, so `1 + 4·lobe` hit ZERO at the default
    // sharpness (0.25) and went NEGATIVE above it — Inf/inverted sharpening
    // clamped into hard black/white speckle on contrast edges. AMD's RCAS
    // limits the lobe to [−(0.25 − 1/16), 0] before applying sharpness.
    const lobeRaw = max(hitMin.negate(), hitMax).negate();
    const lobe = clamp(lobeRaw, float(-RCAS_LOBE_LIMIT), float(0)).mul(sharpnessU);

    const result = n.rgb.add(s.rgb).add(e.rgb).add(w.rgb).mul(lobe).add(c.rgb);
    const denom = float(1).add(lobe.mul(4));
    const sharpened = vec3(
      clamp(result.x.div(denom), minRGB.x, maxRGB.x),
      clamp(result.y.div(denom), minRGB.y, maxRGB.y),
      clamp(result.z.div(denom), minRGB.z, maxRGB.z),
    );
    return vec4(sharpened, c.a);
  });

  return {
    node: rcasFn(),
    uniforms: { sharpness: sharpnessU, invSrcSize: invSrcSizeU },
  };
}
